import { MouseEvent, useCallback } from "react";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import { AsideProvider } from "domains/ui/components/Aside/context";
import AsideSection from "domains/ui/components/Aside/Section";
import ControlButtonSelect, {
  ButtonSelect,
} from "domains/ui/components/ControlButtonSelect";
import ControlSwitch from "domains/ui/components/ControlSwitch";
import Icon from "domains/ui/components/Icon";
import SliderWithInputNumber from "domains/ui/components/SliderWithInputNumber";

import { Box, HStack, VStack } from "@chakra-ui/react";

import { CameraPosition, DisplaySettings, Theme } from "./types";

const THEME_OPTIONS = [
  {
    value: "dark" as Theme,
    label: "Dark",
    tooltip: "Dark studio with almost black background",
  },
  {
    value: "light" as Theme,
    label: "Light",
    tooltip: "Light studio with almost white background",
  },
];

interface SidebarProps {
  settings: DisplaySettings;
  isWireframeProcessing: boolean;
  onSettingsChange: (settings: DisplaySettings) => void;
  onThemeChange: (theme: Theme) => void;
  onCameraPositionChange: (position: CameraPosition) => void;
  onWireframeToggle: () => void;
  onWireframeOnlyToggle: () => void;
  onResetSettings: (e: MouseEvent<HTMLButtonElement>) => void;
}

export function Sidebar({
  settings,
  isWireframeProcessing,
  onSettingsChange,
  onThemeChange,
  onCameraPositionChange,
  onWireframeToggle,
  onWireframeOnlyToggle,
  onResetSettings,
}: SidebarProps) {
  /**
   * Creates a slider change handler for a specific setting key
   * @param key - The display setting key to update
   * @returns Function that handles slider value changes
   */
  const handleSliderChange = useCallback(
    (key: keyof DisplaySettings, value: number | undefined) => {
      if (value === undefined) return;
      onSettingsChange({
        ...settings,
        [key]: value,
      });
    },
    [settings, onSettingsChange]
  );

  return (
    <VStack
      align="stretch"
      flexShrink={0}
      overflow="auto"
      w="300px"
      h="100%"
      bg="backgroundTertiary.500"
      borderRightWidth={1}
      spacing={0}
    >
      <AsideProvider prefix="3d">
        {/* Scene Settings */}
        <AsideSection
          id="sceneSettings"
          title="Scene"
          topRightComponent={
            <ButtonSectionTopIcon onClick={onResetSettings}>
              <Icon id="Ui/Refresh" h="14px" />
            </ButtonSectionTopIcon>
          }
        >
          <VStack align="stretch" spacing={2}>
            <ControlButtonSelect
              title="Theme"
              tooltip="Choose between dark or light studio ambiance"
              options={THEME_OPTIONS}
              selectedOption={settings.theme}
              onOptionSelected={onThemeChange}
            />
            <SliderWithInputNumber
              title="Intensity"
              tooltip="Adjust background and fog darkness/lightness (±50%)"
              value={settings.themeIntensity}
              setValue={handleSliderChange.bind(null, "themeIntensity")}
              min={0.5}
              max={1.5}
              step={0.1}
            />
          </VStack>
        </AsideSection>

        {/* Light Settings */}
        <AsideSection id="lightSettings" title="Light">
          <VStack align="stretch" spacing={2}>
            <SliderWithInputNumber
              title="Intensity"
              value={settings.environmentIntensity}
              setValue={handleSliderChange.bind(null, "environmentIntensity")}
              min={0}
              max={2}
              step={0.1}
            />
            <ControlSwitch
              title={"Shadows"}
              tooltip="Enable real-time shadow casting and receiving"
              isChecked={settings.enableShadows}
              setIsChecked={() =>
                onSettingsChange({
                  ...settings,
                  enableShadows: !settings.enableShadows,
                })
              }
            />
            <SliderWithInputNumber
              title="Brightness"
              value={settings.brightness}
              setValue={handleSliderChange.bind(null, "brightness")}
              min={0.5}
              max={5}
              step={0.1}
            />
            <SliderWithInputNumber
              title="Contrast"
              value={settings.contrast}
              setValue={handleSliderChange.bind(null, "contrast")}
              min={0}
              max={10}
              step={0.1}
            />
            <SliderWithInputNumber
              title="Rim Light"
              tooltip="Accent lighting that creates edge highlighting to separate the subject from the background and add depth"
              value={settings.rimLightIntensity}
              setValue={handleSliderChange.bind(null, "rimLightIntensity")}
              min={0}
              max={2}
              step={0.1}
            />
          </VStack>
        </AsideSection>

        {/* Object Settings */}
        <AsideSection id="objectSettings" title="Object">
          <VStack align="stretch" spacing={2}>
            <ControlSwitch
              title={`Wireframe ${
                isWireframeProcessing
                  ? "(Processing...)"
                  : settings.wireframeOnly
                  ? "(Locked)"
                  : ""
              }`}
              tooltip={
                settings.wireframeOnly
                  ? "Wireframe is automatically enabled in wireframe-only mode"
                  : "Show wireframe overlay on top of materials"
              }
              isChecked={settings.wireframe}
              setIsChecked={onWireframeToggle}
              isDisabled={isWireframeProcessing || settings.wireframeOnly}
            />
            <ControlSwitch
              title={"Wireframe Only"}
              tooltip="Show only wireframe without object colors/materials. Automatically enables wireframe."
              isChecked={settings.wireframeOnly}
              setIsChecked={onWireframeOnlyToggle}
            />
          </VStack>
        </AsideSection>

        {/* Camera Settings */}
        <AsideSection id="cameraSettings" title="Camera">
          <VStack align="stretch" spacing={2}>
            <Box>
              <Box mb={2} color="textPrimary" fontSize="sm" fontWeight="medium">
                Position
              </Box>
              <VStack spacing={2}>
                {/* Default - Full width */}
                <ButtonSelect
                  tooltip="Default camera position with optimal viewing angle"
                  isSelected={settings.cameraPosition === "default"}
                  onClick={() => onCameraPositionChange("default")}
                >
                  Default
                </ButtonSelect>

                {/* Front and Top - Side by side */}
                <HStack w="100%" spacing={2}>
                  <ButtonSelect
                    tooltip="Front view of the object"
                    isSelected={settings.cameraPosition === "front"}
                    onClick={() => onCameraPositionChange("front")}
                  >
                    Front
                  </ButtonSelect>
                  <ButtonSelect
                    tooltip="Top view of the object"
                    isSelected={settings.cameraPosition === "top"}
                    onClick={() => onCameraPositionChange("top")}
                  >
                    Top
                  </ButtonSelect>
                </HStack>

                {/* Left and Right - Side by side */}
                <HStack w="100%" spacing={2}>
                  <ButtonSelect
                    tooltip="Left view of the object"
                    isSelected={settings.cameraPosition === "left"}
                    onClick={() => onCameraPositionChange("left")}
                  >
                    Left
                  </ButtonSelect>
                  <ButtonSelect
                    tooltip="Right view of the object"
                    isSelected={settings.cameraPosition === "right"}
                    onClick={() => onCameraPositionChange("right")}
                  >
                    Right
                  </ButtonSelect>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </AsideSection>
      </AsideProvider>
    </VStack>
  );
}
